import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import type {
  SubsidiaryMappingKeys,
  SubsidiaryMappingWorkflow,
} from '@revelio/core';

import type { CompanyMappingState } from './company-mapping.model';

export const DEFAULT_SUBSIDIARY_COLUMN_MAPPINGS: SubsidiaryMappingWorkflow = {
  previousStep: null,
  mappings: {
    primary: {
      title: 'Primary',
      name: 'Primary',
      selectAll: true,
      isLoading: false,
      subsidiaries: [],
    },
  },
};

export const DEFAULT_SUBSIDIARY_SELECTED_COLUMNS: SubsidiaryMappingKeys[] = [
  'primary',
];

export type CompanyMappingStoreState = {
  companyMapping: CompanyMappingState | null;
  subsidiaryColumnMappings: SubsidiaryMappingWorkflow;
  subsidiarySelectedColumns: SubsidiaryMappingKeys[];
};

export type CompanyMappingStoreActions = {
  setCompanyMapping: (value: CompanyMappingState | null) => void;
  setSubsidiaryColumnMappings: (value: SubsidiaryMappingWorkflow) => void;
  setSubsidiarySelectedColumns: (value: SubsidiaryMappingKeys[]) => void;
  resetState: () => void;
};

const initialState: CompanyMappingStoreState = {
  companyMapping: null,
  subsidiaryColumnMappings: DEFAULT_SUBSIDIARY_COLUMN_MAPPINGS,
  subsidiarySelectedColumns: DEFAULT_SUBSIDIARY_SELECTED_COLUMNS,
};

export const useCompanyMappingStore = create<
  CompanyMappingStoreState & { actions: CompanyMappingStoreActions }
>()(
  devtools(
    (set) => ({
      ...initialState,
      actions: {
        setCompanyMapping: (value) => set({ companyMapping: value }),
        setSubsidiaryColumnMappings: (value) =>
          set({ subsidiaryColumnMappings: value }),
        setSubsidiarySelectedColumns: (value) =>
          set({ subsidiarySelectedColumns: value }),
        resetState: () => set(initialState),
      },
    }),
    { name: 'CompanyMapping' }
  )
);

export const useCompanyMappingSelector = <T>(
  selector: (state: CompanyMappingStoreState) => T
) => useStoreWithEqualityFn(useCompanyMappingStore, selector);
export const selectCompanyMapping = (s: CompanyMappingStoreState) =>
  s.companyMapping;
export const getCompanyMapping = () =>
  useCompanyMappingStore.getState().companyMapping;
export const getSubsidiaryColumnMappings = () =>
  useCompanyMappingStore.getState().subsidiaryColumnMappings;
